/**
 * Time formatting helpers.
 *
 * All timestamps in ChorePop are stored as ISO-8601 UTC strings per
 * docs/CONVENTIONS.md §4. This module is the single display bridge —
 * every "2 hours ago" / "Yesterday" string flows through here so tone
 * and locale stay consistent.
 */

const MIN = 60;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

export function formatRelative(
  whenIso: string,
  nowIso: string = new Date().toISOString(),
  locale = 'en-US',
): string {
  const when = Date.parse(whenIso);
  const now = Date.parse(nowIso);
  if (Number.isNaN(when) || Number.isNaN(now)) {
    throw new Error(`invalid ISO timestamp: ${whenIso}`);
  }

  const diffSec = Math.round((now - when) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (Math.abs(diffSec) < MIN) return rtf.format(-Math.round(diffSec), 'second');
  if (Math.abs(diffSec) < HOUR) return rtf.format(-Math.round(diffSec / MIN), 'minute');
  if (Math.abs(diffSec) < DAY) return rtf.format(-Math.round(diffSec / HOUR), 'hour');
  if (Math.abs(diffSec) < 7 * DAY) return rtf.format(-Math.round(diffSec / DAY), 'day');
  if (Math.abs(diffSec) < 30 * DAY) return rtf.format(-Math.round(diffSec / (7 * DAY)), 'week');
  return rtf.format(-Math.round(diffSec / (30 * DAY)), 'month');
}

/** Time-of-day bucket for greeting copy (REQ-1.2). */
export type Daypart = 'morning' | 'afternoon' | 'evening';

export function daypartOf(date: Date = new Date()): Daypart {
  const h = date.getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

export function greetingFor(date: Date = new Date()): string {
  const part = daypartOf(date);
  return part === 'morning'
    ? 'Good morning'
    : part === 'afternoon'
      ? 'Good afternoon'
      : 'Good evening';
}
